/* Prices and valid allocations are rendered by Shopify for the active market.
 * Quick View owns its input; on product pages the app continues to own it. */
if (!customElements.get('purchase-options')) {
  customElements.define('purchase-options', class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('product-info');
      this.form = this.closest('form');
      if (!this.info || !this.form) return;
      this.native = this.dataset.native === 'true';
      this.baseDisabled = !!this.form.querySelector('[name="add"]')?.disabled;
      this.onChange = (event) => {
        if (event.target.matches('[data-plan-select]')) {
          this.setNativePlan(event.target.value);
        }
        this.scheduleSync();
      };
      this.onSubmit = (event) => {
        if (event.target === this.form && !this.validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      this.info.addEventListener('change', this.onChange);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.observer = new MutationObserver((records) => {
        // Hidden input.value is reflected to its value attribute. Also cover late app injection.
        if (records.some((record) => record.target.matches?.('[name="selling_plan"]') ||
          [...record.addedNodes, ...record.removedNodes].some((node) =>
            node.matches?.('[name="selling_plan"]') || node.querySelector?.('[name="selling_plan"]')))) {
          this.scheduleSync();
        }
      });
      this.observer.observe(this.info, { subtree: true, childList: true, attributes: true, attributeFilter: ['value', 'disabled', 'name'] });
      this.scheduleSync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.info?.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('submit', this.onSubmit, true);
    }

    scheduleSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      queueMicrotask(() => {
        this.syncQueued = false;
        if (this.isConnected) this.sync();
      });
    }

    get state() { return this.querySelector('[data-purchase-state]'); }
    get planInputs() { return Array.from(this.form.elements).filter((input) => input.name === 'selling_plan' && !input.disabled); }
    get planId() { return this.planInputs[0]?.value || ''; }
    get plans() { return Array.from(this.querySelectorAll('template[data-plan-price]')); }
    template(planId, type = 'price') {
      return this.querySelector(`template[data-plan-${type}="${CSS.escape(String(planId))}"]`);
    }

    setNativePlan(planId) {
      const input = this.querySelector('input[name="selling_plan"]');
      if (!input) return;
      if (input.value !== planId) input.value = planId;
      input.disabled = !planId;
      const select = this.querySelector('[data-plan-select]');
      if (select) select.value = planId;
    }

    beginVariantChange() {
      if (!this.pending) this.previousPlan = this.planId;
      this.pending = true;
      if (this.native) this.setNativePlan('');
      this.sync();
    }

    commitVariant(html) {
      const next = html.querySelector('purchase-options [data-purchase-state]');
      if (!next) return; // Remain blocked if no authoritative allocation data was returned.
      this.state.replaceWith(next.cloneNode(true));
      this.pending = false;
      this.baseDisabled = !!html.querySelector('form[is="product-form"] [name="add"]')?.disabled;
      if (this.native) {
        let plan = this.previousPlan || '';
        if (!this.template(plan) || (!plan && this.state.dataset.required === 'true')) {
          plan = this.state.dataset.required === 'true' ? this.plans.find((item) => item.dataset.planPrice)?.dataset.planPrice || '' : '';
        }
        this.setNativePlan(plan);
      }
      this.sync();
    }

    isValid() {
      const state = this.state;
      const id = this.form.elements.namedItem('id')?.value;
      const plan = this.planId;
      return !!state && !this.pending && !this.baseDisabled && id === state.dataset.variantId &&
        state.dataset.available === 'true' && !!this.template(plan) &&
        (state.dataset.required !== 'true' || !!plan) &&
        this.planInputs.every((input) => input.value === plan);
    }

    validate() {
      this.sync();
      if (this.isValid()) return true;
      this.form.displayFormErrors?.(this.dataset.error);
      return false;
    }

    sync() {
      if (!this.state || !this.form) return;
      const valid = this.isValid();
      const variantMatches = this.form.elements.namedItem('id')?.value === this.state.dataset.variantId;
      let plan = this.planId;
      // Before the official app initializes, display a valid required allocation as the default,
      // but do not authorize purchase until the app has supplied a valid form input.
      if (!plan && this.state.dataset.required === 'true') {
        plan = this.plans.find((item) => item.dataset.planPrice)?.dataset.planPrice || '';
      }
      const price = this.info.querySelector('[id^="price-"]');
      const source = this.template(plan);
      const showPrice = !this.pending && variantMatches && source &&
        (this.state.dataset.required !== 'true' || !!plan);
      const details = this.querySelector('[data-plan-details-display]');
      const detailSource = this.template(plan, 'details');
      if (details) {
        const html = showPrice && detailSource ? detailSource.innerHTML : '';
        if (details.innerHTML !== html) details.innerHTML = html;
        details.hidden = !html.trim();
      }
      this.classList.toggle('hidden', !this.querySelector('[data-plan-select]') && (!details || details.hidden));
      if (price) {
        price.classList.toggle('hidden', !showPrice);
        price.setAttribute('aria-busy', String(!!this.pending));
        if (showPrice && price.innerHTML !== source.innerHTML) price.innerHTML = source.innerHTML;
      }
      const badges = this.info.querySelector('[id^="Badges-"]');
      const badgeSource = this.template(plan, 'badges');
      if (badges) {
        badges.classList.toggle('hidden', !showPrice);
        if (showPrice && badgeSource && badges.innerHTML !== badgeSource.innerHTML) badges.innerHTML = badgeSource.innerHTML;
      }
      this.setButtonBlocked(this.form.querySelector('[name="add"]'), !valid);
      this.syncSticky(valid, showPrice ? source : null);
    }

    setButtonBlocked(button, blocked) {
      if (!button) return;
      button.disabled = blocked;
    }

    syncSticky(valid, source) {
      if (this.info.dataset.viewMode === 'quick-view') return;
      document.querySelectorAll('sticky-atc-bar').forEach((sticky) => {
        if (sticky.dataset.productId !== this.info.dataset.productId ||
          (sticky.mainProductInfo && sticky.mainProductInfo !== this.info)) return;
        const form = sticky.querySelector('form');
        if (!form) return;
        const matches = form.elements.namedItem('id')?.value === this.state.dataset.variantId;
        let input = form.querySelector('[data-purchase-plan]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden'; input.name = 'selling_plan'; input.dataset.purchasePlan = '';
          form.appendChild(input);
        }
        input.value = valid && matches ? this.planId : '';
        input.disabled = !input.value;
        this.setButtonBlocked(form.querySelector('[name="add"]'), !valid || !matches);
        const price = sticky.querySelector('.f-price');
        if (price) {
          price.classList.toggle('hidden', !source || !matches);
          if (source && matches) {
            const replacement = source.content.querySelector('.f-price').cloneNode(true);
            replacement.classList.remove('f-price--large');
            if (price.outerHTML !== replacement.outerHTML) price.replaceWith(replacement);
          }
        }
      });
    }
  });
}
